// Reusable "[" / "]" tab-cycling for any tabbed surface. Pushes the "tabs" scope
// (so the hint bar shows "[ ]  Switch tab") and wires the prev/next shortcuts to
// a caller-supplied selector. Works for route-, query- or state-driven tabs: the
// caller owns how a tab index maps to an action, this hook only owns the keys.
//
// Page with a single tab strip (only one mounted at a time) — ignore the return:
//   useTabCycle(tabs.length, activeIndex, (i) => navigate(tabs[i].path));
//
// Panel that shares the screen with OTHER tab strips (e.g. the trading page):
// pass `{ gated: true }` and attach the returned ref to the panel's container so
// [ / ] only act while the pointer is over that panel. Non-hovered strips
// decline and the key falls through to whichever panel is hovered (or nothing).
//   const gateRef = useTabCycle<HTMLDivElement>(n, active, onSelect, { gated: true });
//   return <Panel ref={gateRef}>…</Panel>;

import { RefObject, useRef } from "react";
import { useKeyboardScope, useShortcut, useShortcuts } from "./useKeyboard";
import { TAB_JUMP_IDS } from "./shortcuts";

export interface TabCycleOptions {
  /**
   * Only fire while the pointer is over the returned ref's element, so several
   * tab strips can coexist on one page. Omit for a page with one tab strip.
   */
  gated?: boolean;
}

export function useTabCycle<E extends HTMLElement = HTMLElement>(
  count: number,
  activeIndex: number,
  onSelect: (index: number) => void,
  options?: TabCycleOptions,
): RefObject<E> {
  useKeyboardScope("tabs");
  const gateRef = useRef<E>(null);

  // Keep the latest inputs in a ref so the (identity-free) shortcut handlers
  // always see fresh values without re-registering on every render.
  const state = useRef({ count, activeIndex, onSelect, gated: options?.gated });
  state.current = { count, activeIndex, onSelect, gated: options?.gated };

  // Returns true when it consumed the key, false to pass through to the next
  // bound strip (gated + not hovered, or nothing to cycle).
  const cycle = (delta: number): boolean => {
    const { count: n, activeIndex: active, onSelect: select, gated } = state.current;
    if (gated && !gateRef.current?.matches(":hover")) return false;
    if (n <= 0) return false;
    const from = active < 0 || active >= n ? 0 : active;
    select((from + delta + n) % n);
    return true;
  };

  useShortcut("tabs.prev", () => cycle(-1));
  useShortcut("tabs.next", () => cycle(1));

  // 1–9 jump straight to the Nth tab (the digit comes from the event). Same
  // hover gate as cycling; declines when the digit is out of range.
  useShortcuts(TAB_JUMP_IDS, (e) => {
    const { count: n, onSelect: select, gated } = state.current;
    if (gated && !gateRef.current?.matches(":hover")) return false;
    const idx = Number(e.key) - 1;
    if (idx < 0 || idx >= n) return false;
    select(idx);
    return true;
  });

  return gateRef;
}
